import Phaser from 'phaser'
import { BuildingId, GameState, UpgradeEffectType, UpgradeId, BuildingProductionBreakdown } from '../types'
import { GameEvents } from '../events/GameEvents'
import { formatNumber, formatTPS } from '../utils/decimal'
import { calculateTPS, getBuildingCount, calculateBuildingCost, canAffordBuilding, calculateBuildingProductionBreakdown } from '../systems/buildingSystem'
import { calculateClickValue } from '../systems/tokenSystem'
import { canAffordUpgrade } from '../systems/upgradeSystem'
import { getVisibleBuildings, getVisibleUpgrades } from '../systems/visibilitySystem'
import { buildings, BUILDING_ORDER } from '../data/buildings'
import './game-ui.css'

const MAX_VISIBLE_UPGRADES = 50

type CachedElements = {
  readonly tokensText: HTMLElement
  readonly tpsText: HTMLElement
  readonly perClickText: HTMLElement
  readonly clickButtonContainer: HTMLElement
  readonly buildingRows: Map<BuildingId, BuildingRowElements>
  readonly upgradeItems: UpgradeItemElements[]
}

type BuildingRowElements = {
  readonly row: HTMLElement
  readonly nameText: HTMLElement
  readonly countText: HTMLElement
  readonly productionText: HTMLElement
  readonly costText: HTMLElement
  readonly buyButton: HTMLButtonElement
  readonly tooltip: HTMLElement
}

type UpgradeItemElements = {
  readonly item: HTMLElement
  readonly nameText: HTMLElement
  readonly costText: HTMLElement
  readonly targetText: HTMLElement
  readonly descText: HTMLElement
}

type CachedValues = {
  tokens: string
  tps: string
  perClick: string
  clickValueDisplay: string
  buildingStates: Map<BuildingId, { visible: boolean; unlocked: boolean; affordable: boolean; count: string; cost: string; production: string }>
  upgradeStates: Array<{ id: UpgradeId; affordable: boolean; name: string; cost: string; target: string; desc: string } | null>
}

export class UIManager {
  private readonly scene: Phaser.Scene
  private domElement: Phaser.GameObjects.DOMElement | null = null
  private cachedElements: CachedElements | null = null
  private cachedValues: CachedValues = {
    tokens: '',
    tps: '',
    perClick: '',
    clickValueDisplay: '1',
    buildingStates: new Map(),
    upgradeStates: [],
  }

  constructor(scene: Phaser.Scene) {
    this.scene = scene
  }

  create(): void {
    const html = this.buildHTML()
    this.domElement = this.scene.add.dom(0, 0).createFromHTML(html)
    this.domElement.setOrigin(0, 0)

    this.cacheElements()
    this.wireEventListeners()
  }

  private buildHTML(): string {
    return `
      <div class="game-ui">
        <div class="left-panel">
          <div class="token-display">
            <div class="tokens-text" data-element="tokens">0</div>
            <div class="tokens-label">tokens</div>
            <div class="tps-text" data-element="tps">TPS: 0.0/sec</div>
            <div class="per-click-text" data-element="per-click">Per Prompt: 1</div>
          </div>
          <div class="click-button-container">
            <button class="click-button" data-action="click">PROMPT</button>
          </div>
          <button class="reset-button" data-action="reset">Reset</button>
          <div class="branding">
            <div class="branding-version">v${__APP_VERSION__}</div>
            <div class="branding-name">HAMY LABS - <a href="https://hamy.xyz" target="_blank">hamy.xyz</a></div>
          </div>
        </div>
        <div class="right-panel">
          <div class="building-panel">
            ${this.buildBuildingRowsHTML()}
          </div>
          <div class="upgrade-panel">
            ${this.buildUpgradeSlotsHTML()}
          </div>
        </div>
      </div>
    `
  }

  private buildUpgradeSlotsHTML(): string {
    let html = ''
    for (let i = 0; i < MAX_VISIBLE_UPGRADES; i++) {
      html += `
        <div class="upgrade-item hidden" data-upgrade-index="${i}">
          <div class="upgrade-name" data-element="upgrade-name-${i}"></div>
          <div class="upgrade-cost" data-element="upgrade-cost-${i}"></div>
          <div class="upgrade-target" data-element="upgrade-target-${i}"></div>
          <div class="upgrade-desc" data-element="upgrade-desc-${i}"></div>
        </div>
      `
    }
    return html
  }

  private buildBuildingRowsHTML(): string {
    let html = ''
    for (const buildingId of BUILDING_ORDER) {
      const building = buildings.get(buildingId)
      if (building === undefined) continue

      html += `
        <div class="building-row hidden" data-building-id="${buildingId}">
          <div class="building-info">
            <div class="building-header">
              <span class="building-name" data-element="building-name-${buildingId}">${building.name}</span>
              <span class="building-count" data-element="building-count-${buildingId}">x0</span>
            </div>
            <div class="building-production" data-element="building-production-${buildingId}">+${formatNumber(building.baseProduction)}/sec each</div>
            <div class="building-cost" data-element="building-cost-${buildingId}">Cost: ${formatNumber(building.baseCost)}</div>
          </div>
          <button class="building-buy-btn affordable" data-action="buy-building" data-building-id="${buildingId}">Buy</button>
          <div class="building-tooltip" data-element="building-tooltip-${buildingId}"></div>
        </div>
      `
    }
    return html
  }

  private cacheElements(): void {
    if (this.domElement === null) return

    const container = this.domElement.node as HTMLElement

    const tokensText = container.querySelector('[data-element="tokens"]') as HTMLElement
    const tpsText = container.querySelector('[data-element="tps"]') as HTMLElement
    const perClickText = container.querySelector('[data-element="per-click"]') as HTMLElement
    const clickButtonContainer = container.querySelector('.click-button-container') as HTMLElement

    const buildingRows = new Map<BuildingId, BuildingRowElements>()
    for (const buildingId of BUILDING_ORDER) {
      const row = container.querySelector(`[data-building-id="${buildingId}"]`) as HTMLElement
      if (row === null) continue

      buildingRows.set(buildingId, {
        row,
        nameText: container.querySelector(`[data-element="building-name-${buildingId}"]`) as HTMLElement,
        countText: container.querySelector(`[data-element="building-count-${buildingId}"]`) as HTMLElement,
        productionText: container.querySelector(`[data-element="building-production-${buildingId}"]`) as HTMLElement,
        costText: container.querySelector(`[data-element="building-cost-${buildingId}"]`) as HTMLElement,
        buyButton: row.querySelector('[data-action="buy-building"]') as HTMLButtonElement,
        tooltip: container.querySelector(`[data-element="building-tooltip-${buildingId}"]`) as HTMLElement,
      })
    }

    const upgradeItems: UpgradeItemElements[] = []
    for (let i = 0; i < MAX_VISIBLE_UPGRADES; i++) {
      const item = container.querySelector(`[data-upgrade-index="${i}"]`) as HTMLElement
      if (item === null) continue

      upgradeItems.push({
        item,
        nameText: container.querySelector(`[data-element="upgrade-name-${i}"]`) as HTMLElement,
        costText: container.querySelector(`[data-element="upgrade-cost-${i}"]`) as HTMLElement,
        targetText: container.querySelector(`[data-element="upgrade-target-${i}"]`) as HTMLElement,
        descText: container.querySelector(`[data-element="upgrade-desc-${i}"]`) as HTMLElement,
      })
    }

    this.cachedElements = {
      tokensText,
      tpsText,
      perClickText,
      clickButtonContainer,
      buildingRows,
      upgradeItems,
    }
  }

  private wireEventListeners(): void {
    if (this.domElement === null) return

    const container = this.domElement.node as HTMLElement

    container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const action = target.dataset.action

      if (action === 'click') {
        this.spawnClickValuePopup()
        this.scene.game.events.emit(GameEvents.CLICK)
        return
      }

      if (action === 'reset') {
        this.scene.game.events.emit(GameEvents.RESET)
        return
      }

      if (action === 'buy-building') {
        const buildingId = target.dataset.buildingId as BuildingId | undefined
        if (buildingId !== undefined) {
          this.scene.game.events.emit(GameEvents.BUILDING_PURCHASE, { buildingId })
        }
        return
      }

      // Check for upgrade clicks (delegate from parent)
      const upgradeItem = target.closest('[data-upgrade-index]') as HTMLElement | null
      if (upgradeItem !== null) {
        const upgradeId = upgradeItem.dataset.upgradeId as UpgradeId | undefined
        if (upgradeId !== undefined) {
          this.scene.game.events.emit(GameEvents.UPGRADE_PURCHASE, { upgradeId })
        }
      }
    })
  }

  private spawnClickValuePopup(): void {
    if (this.cachedElements === null) return

    const container = this.cachedElements.clickButtonContainer
    const popup = document.createElement('div')
    popup.className = 'click-value-popup'
    popup.textContent = `+${this.cachedValues.clickValueDisplay}`

    // Position above the button with slight random horizontal offset
    const offsetX = Math.random() * 40 - 20
    popup.style.left = `calc(50% + ${offsetX}px)`
    popup.style.bottom = '70%'
    popup.style.transform = 'translateX(-50%)'

    container.appendChild(popup)

    popup.addEventListener('animationend', () => {
      popup.remove()
    })
  }

  update(state: GameState): void {
    this.updateTokenDisplay(state)
    this.updateBuildingPanel(state)
    this.updateUpgradePanel(state)
  }

  private updateTokenDisplay(state: GameState): void {
    if (this.cachedElements === null) return

    const tokensStr = formatNumber(state.tokens)
    if (this.cachedValues.tokens !== tokensStr) {
      this.cachedValues.tokens = tokensStr
      this.cachedElements.tokensText.textContent = tokensStr
    }

    const tps = calculateTPS(state)
    const tpsStr = `TPS: ${formatTPS(tps)}/sec`
    if (this.cachedValues.tps !== tpsStr) {
      this.cachedValues.tps = tpsStr
      this.cachedElements.tpsText.textContent = tpsStr
    }

    const clickValue = calculateClickValue(state)
    const clickValueDisplay = formatNumber(clickValue)
    this.cachedValues.clickValueDisplay = clickValueDisplay
    const perClickStr = `Per Prompt: ${clickValueDisplay}`
    if (this.cachedValues.perClick !== perClickStr) {
      this.cachedValues.perClick = perClickStr
      this.cachedElements.perClickText.textContent = perClickStr
    }
  }

  private updateBuildingPanel(state: GameState): void {
    if (this.cachedElements === null) return

    const visibleBuildings = getVisibleBuildings(state)
    const visibleBuildingIds = new Set(visibleBuildings.map((vb) => vb.building.id))

    // Hide buildings that are no longer visible
    for (const [buildingId, elements] of this.cachedElements.buildingRows) {
      const cachedState = this.cachedValues.buildingStates.get(buildingId)
      const shouldBeVisible = visibleBuildingIds.has(buildingId)

      if (!shouldBeVisible) {
        if (cachedState?.visible !== false) {
          elements.row.classList.add('hidden')
          this.cachedValues.buildingStates.set(buildingId, {
            visible: false,
            unlocked: false,
            affordable: false,
            count: '',
            cost: '',
            production: '',
          })
        }
      }
    }

    // Update visible buildings
    for (const { building, isUnlocked } of visibleBuildings) {
      const elements = this.cachedElements.buildingRows.get(building.id)
      if (elements === undefined) continue

      const owned = getBuildingCount(state, building.id)
      const cost = calculateBuildingCost(building, owned)
      const isAffordable = canAffordBuilding(state, building.id)

      const countStr = `x${formatNumber(owned)}`
      const costStr = `Cost: ${formatNumber(cost)}`

      // Calculate effective production for cache check and display
      const tps = calculateTPS(state)
      const breakdown = calculateBuildingProductionBreakdown(state, building.id, tps)
      const productionStr =
        breakdown !== null
          ? `+${formatTPS(breakdown.baseProductionPerUnit.mul(breakdown.totalMultiplier))}/sec each`
          : `+${formatNumber(building.baseProduction)}/sec each`

      const cachedState = this.cachedValues.buildingStates.get(building.id)
      const stateChanged =
        cachedState === undefined ||
        cachedState.visible !== true ||
        cachedState.unlocked !== isUnlocked ||
        cachedState.affordable !== isAffordable ||
        cachedState.count !== countStr ||
        cachedState.cost !== costStr ||
        cachedState.production !== productionStr

      if (stateChanged) {
        elements.row.classList.remove('hidden')
        elements.row.classList.remove('unaffordable', 'locked')

        if (isUnlocked) {
          elements.nameText.textContent = building.name
          elements.countText.textContent = countStr
          elements.costText.textContent = costStr
          elements.productionText.textContent = productionStr

          if (breakdown !== null) {
            elements.tooltip.innerHTML = this.formatBuildingTooltip(breakdown)
          }

          elements.buyButton.classList.remove('affordable', 'unaffordable', 'locked')
          if (isAffordable) {
            elements.buyButton.classList.add('affordable')
          } else {
            elements.row.classList.add('unaffordable')
            elements.buyButton.classList.add('unaffordable')
          }
          elements.buyButton.textContent = 'Buy'
        } else {
          elements.row.classList.add('locked')
          elements.nameText.textContent = '???'
          elements.countText.textContent = ''
          elements.productionText.textContent = '???'
          elements.costText.textContent = '???'
          elements.buyButton.classList.remove('affordable', 'unaffordable')
          elements.buyButton.classList.add('locked')
          elements.buyButton.textContent = '???'
          elements.tooltip.innerHTML = ''
        }

        this.cachedValues.buildingStates.set(building.id, {
          visible: true,
          unlocked: isUnlocked,
          affordable: isAffordable,
          count: countStr,
          cost: costStr,
          production: productionStr,
        })
      }
    }
  }

  private formatUpgradeTarget(effect: UpgradeEffectType): string {
    switch (effect.type) {
      case 'doubler': {
        if (effect.targetBuilding === 'click') return 'Click'
        const building = buildings.get(effect.targetBuilding)
        return building?.name ?? effect.targetBuilding
      }
      case 'synergy': {
        const target = effect.targetBuilding === 'click'
          ? 'Click'
          : buildings.get(effect.targetBuilding)?.name ?? effect.targetBuilding
        const source = effect.sourceBuilding === 'all'
          ? 'All'
          : buildings.get(effect.sourceBuilding)?.name ?? effect.sourceBuilding
        return `${target} (+${effect.bonusPercentage}% per ${source})`
      }
      case 'clickScaling':
        return `Click (+${effect.tpsPercentage}% TPS/click)`
    }
  }

  private formatBuildingTooltip(breakdown: BuildingProductionBreakdown): string {
    if (breakdown.quantity.eq(0)) {
      return `<div class="tooltip-row">No ${breakdown.buildingName}s owned yet</div>`
    }

    const effectivePerUnit = breakdown.baseProductionPerUnit.mul(breakdown.totalMultiplier)

    const html = `
      <div class="tooltip-header">${breakdown.buildingName} Production</div>
      <div class="tooltip-section">
        <div class="tooltip-row">
          <span>Production per unit:</span>
          <span>${formatTPS(effectivePerUnit)}/sec each</span>
        </div>
        <div class="tooltip-row">
          <span>Quantity:</span>
          <span>x${formatNumber(breakdown.quantity)}</span>
        </div>
      </div>
      <div class="tooltip-section tooltip-final">
        <div class="tooltip-row">
          <span>Total Production:</span>
          <span>${formatTPS(breakdown.finalProduction)}/sec</span>
        </div>
        <div class="tooltip-row">
          <span>% of Total TPS:</span>
          <span>${breakdown.percentageOfTotalTPS.toFixed(1)}%</span>
        </div>
      </div>
    `

    return html
  }

  refreshAllTooltips(state: GameState): void {
    if (this.cachedElements === null) return

    const tps = calculateTPS(state)
    const visibleBuildings = getVisibleBuildings(state)

    for (const { building, isUnlocked } of visibleBuildings) {
      if (!isUnlocked) continue
      const elements = this.cachedElements.buildingRows.get(building.id)
      if (elements === undefined) continue

      const breakdown = calculateBuildingProductionBreakdown(state, building.id, tps)
      if (breakdown !== null) {
        elements.tooltip.innerHTML = this.formatBuildingTooltip(breakdown)
      }
    }
  }

  private updateUpgradePanel(state: GameState): void {
    if (this.cachedElements === null) return

    const visibleUpgrades = getVisibleUpgrades(state)

    for (let i = 0; i < this.cachedElements.upgradeItems.length; i++) {
      const elements = this.cachedElements.upgradeItems[i]
      const visibleUpgrade = visibleUpgrades[i]

      if (visibleUpgrade === undefined) {
        const cachedState = this.cachedValues.upgradeStates[i]
        if (cachedState !== null && cachedState !== undefined) {
          elements.item.classList.add('hidden')
          elements.item.removeAttribute('data-upgrade-id')
          this.cachedValues.upgradeStates[i] = null
        }
        continue
      }

      const { upgrade } = visibleUpgrade
      const isAffordable = canAffordUpgrade(state, upgrade.id)
      const nameStr = upgrade.name
      const costStr = `Cost: ${formatNumber(upgrade.cost)}`
      const targetStr = this.formatUpgradeTarget(upgrade.effect)
      const descStr = upgrade.description

      const cachedState = this.cachedValues.upgradeStates[i]
      const stateChanged =
        cachedState === null ||
        cachedState === undefined ||
        cachedState.id !== upgrade.id ||
        cachedState.affordable !== isAffordable ||
        cachedState.name !== nameStr ||
        cachedState.cost !== costStr ||
        cachedState.target !== targetStr ||
        cachedState.desc !== descStr

      if (stateChanged) {
        elements.item.classList.remove('hidden', 'affordable', 'unaffordable')
        elements.item.setAttribute('data-upgrade-id', upgrade.id)

        if (isAffordable) {
          elements.item.classList.add('affordable')
        } else {
          elements.item.classList.add('unaffordable')
        }

        elements.nameText.textContent = nameStr
        elements.costText.textContent = costStr
        elements.targetText.textContent = targetStr
        elements.descText.textContent = descStr

        this.cachedValues.upgradeStates[i] = {
          id: upgrade.id,
          affordable: isAffordable,
          name: nameStr,
          cost: costStr,
          target: targetStr,
          desc: descStr,
        }
      }
    }
  }
}
